import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { BRAND } from "../lib/brand";

const SITE_URL = BRAND.url;
const OG_IMAGE = `${SITE_URL}/og.png`;

export default function Seo({
  title,
  description = BRAND.description,
  path = "/",
  image = OG_IMAGE,
  type = "website",
  schema,
  schemas,
  canonical,
  noindex = false,
}) {
  // Avoid duplicating the brand name if the caller already included it.
  const fullTitle = title
    ? (title.toLowerCase().includes(BRAND.name.toLowerCase()) ? title : `${title} | ${BRAND.name}`)
    : `${BRAND.name} — Websites & Local SEO for US Trades · Lincoln Metro, Nebraska`;
  const url = canonical || `${SITE_URL}${path}`;
  const ld = schema || (schemas?.length ? { "@context": "https://schema.org", "@graph": schemas } : null);
  // react-helmet-async v3 on React 19 hoists tags natively and does NOT remove the
  // static data-rh fallback tags from public/index.html -> strip them once rendered
  // so each page exposes exactly one canonical / description / og:* set.
  useEffect(() => {
    document.querySelectorAll('head [data-rh="true"]').forEach((n) => n.remove());
  }, []);
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="en_GB" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {/* Geo / local SEO */}
      <meta name="geo.region" content="US-NE" />
      <meta name="geo.placename" content={BRAND.addressLocality} />
      <meta name="geo.position" content={`${BRAND.geo.latitude};${BRAND.geo.longitude}`} />
      <meta name="ICBM" content={`${BRAND.geo.latitude}, ${BRAND.geo.longitude}`} />
      {ld && (
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      )}
    </Helmet>
  );
}

// === Reusable schema builders ==============================================

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BRAND.url}#organization`,
  name: BRAND.name,
  url: BRAND.url,
  logo: `${BRAND.url}/logo.png`,
  description: BRAND.description,
  email: BRAND.email,
  telephone: BRAND.phoneDisplay,
  address: {
    "@type": "PostalAddress",
    addressLocality: BRAND.addressLocality,
    addressRegion: BRAND.addressRegion,
    addressCountry: BRAND.addressCountry,
  },
  foundingDate: BRAND.founded,
  areaServed: [
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "United Kingdom" },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: BRAND.email,
      telephone: BRAND.phoneDisplay,
      areaServed: "US",
      availableLanguage: ["English"],
    },
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: BRAND.email,
      telephone: BRAND.uk.phoneDisplay,
      areaServed: "GB",
      availableLanguage: ["English"],
    },
  ],
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${BRAND.url}#localbusiness`,
  name: BRAND.name,
  image: `${BRAND.url}/og.png`,
  url: BRAND.url,
  telephone: BRAND.phoneDisplay,
  email: BRAND.email,
  priceRange: "$199–$499",
  description: BRAND.description,
  address: {
    "@type": "PostalAddress",
    addressLocality: BRAND.addressLocality,
    addressRegion: BRAND.addressRegion,
    postalCode: BRAND.postalCode,
    addressCountry: BRAND.addressCountry,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: BRAND.geo.latitude,
    longitude: BRAND.geo.longitude,
  },
  areaServed: [
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "City", name: "Lincoln" },
    { "@type": "City", name: "Omaha" },
    { "@type": "City", name: "Kansas City" },
    { "@type": "City", name: "Des Moines" },
  ],
  openingHoursSpecification: [{
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "18:00",
  }],
  sameAs: Object.values(BRAND.social || {}),
  department: [{
    "@type": "ProfessionalService",
    name: `${BRAND.name} UK`,
    telephone: BRAND.uk.phoneDisplay,
    email: BRAND.email,
    url: BRAND.url,
    address: { "@type": "PostalAddress", addressLocality: "Luton", addressRegion: "England", addressCountry: "GB" },
    areaServed: { "@type": "Country", name: "United Kingdom" },
  }],
};

// Real, published packages only (from /api/pricing-packages).
export function offerCatalogSchema(packages) {
  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: "Website & SEO packages",
    url: `${BRAND.url}/pricing`,
    itemListElement: packages.map((p) => ({
      "@type": "Offer",
      name: p.name,
      description: p.description,
      price: String(p.price || "").replace(/[^0-9.]/g, ""),
      priceCurrency: "USD",
      url: `${BRAND.url}/pricing`,
      seller: { "@id": `${BRAND.url}#organization` },
      areaServed: ["US", "GB"],
      ...(/month/i.test(p.cadence || "") && {
        priceSpecification: { "@type": "UnitPriceSpecification", price: String(p.price || "").replace(/[^0-9.]/g, ""), priceCurrency: "USD", unitCode: "MON" },
      }),
    })),
  };
}

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BRAND.url}#website`,
  name: BRAND.name,
  url: BRAND.url,
  publisher: { "@id": `${BRAND.url}#organization` },
  inLanguage: "en-US",
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${BRAND.url}/blog?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${BRAND.url}${item.path}`,
    })),
  };
}

export function serviceSchema({ name, description, url, slug, areaServed }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    serviceType: name,
    description,
    url: url || `${BRAND.url}/${slug}`,
    provider: { "@id": `${BRAND.url}#organization` },
    areaServed: areaServed || [{ "@type": "Country", name: "United States" }, { "@type": "Country", name: "United Kingdom" }],
    audience: { "@type": "BusinessAudience", audienceType: "Small businesses, trades and home-service companies in the US and UK" },
    offers: { "@type": "Offer", priceCurrency: "USD", availability: "https://schema.org/InStock" },
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export function articleSchema(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    author: { "@type": "Organization", name: BRAND.name, url: BRAND.url },
    publisher: { "@id": `${BRAND.url}#organization` },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${BRAND.url}/blog/${post.slug}` },
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    inLanguage: "en-US",
  };
}
